import { createSignal, createEffect, createMemo, For, Show, untrack, onMount } from 'solid-js';
import { useSearchParams } from '@solidjs/router';
import Text from '../components/Text';
import { EmptyState } from '../components/EmptyState';
import { SearchInput } from '../components/SearchInput';
import { useApi, isOk } from '../api';
import { useToast } from '../components/MessageBox';
import { useTranslation } from '../hooks/useTranslation';
import { useActiveBot } from '../state/activeBot';
import { formatDateTime, formatDate } from '../services/dateFormat';
import { formatMinecraftText } from '../components/MinecraftText';
import styles from './ChatLogPage.module.css';
import { firstError } from '../api/envelope';

interface ChatEntry { id: string; createdAt: string; message: string; }
interface SearchMatch { match: ChatEntry; before: ChatEntry[]; after: ChatEntry[]; }

const isoLocal = (d: Date) => {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const presets: { key: string; hours: number | null }[] = [
    { key: 'BOT_CHATLOG_PRESET_1H', hours: 1 },
    { key: 'BOT_CHATLOG_PRESET_24H', hours: 24 },
    { key: 'BOT_CHATLOG_PRESET_7D', hours: 24 * 7 },
    { key: 'BOT_CHATLOG_PRESET_ALL', hours: null },
];

const PAGE_SIZE = 200;

export function ChatLogPage() {
    const api = useApi();
    const toast = useToast();
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const { botId: activeBotId, bot: activeBot, setBot } = useActiveBot();

    const queryBotId = () => (searchParams.botId as string | undefined) ?? undefined;
    createEffect(() => {
        const q = queryBotId();
        if (q && activeBotId() !== q) setBot(q);
    });

    const [from, setFrom] = createSignal(isoLocal(new Date(Date.now() - 24 * 3600 * 1000)));
    const [to, setTo] = createSignal(isoLocal(new Date()));
    const [entries, setEntries] = createSignal<ChatEntry[]>([]);
    const [total, setTotal] = createSignal(0);
    const [page, setPage] = createSignal(0);
    const [loading, setLoading] = createSignal(false);
    const [search, setSearch] = createSignal('');
    const [matches, setMatches] = createSignal<SearchMatch[]>([]);
    const [searchTotal, setSearchTotal] = createSignal(0);
    const [anchorId, setAnchorId] = createSignal<string | null>(null);
    const [searchUsesTimeFilter, setSearchUsesTimeFilter] = createSignal(false);
    let listEl: HTMLDivElement | undefined;
    let reqSeq = 0;

    const botId = () => activeBotId() ?? '';
    const isSearching = () => search().trim().length > 0;
    const pageCount = () => Math.max(1, Math.ceil(total() / PAGE_SIZE));

    type Row = { kind: 'day'; key: string; label: string } | { kind: 'msg'; key: string; entry: ChatEntry };
    const rows = createMemo<Row[]>(() => {
        const out: Row[] = [];
        let lastDay: string | null = null;
        for (const entry of [...entries()].reverse()) {
            const day = formatDate(entry.createdAt);
            if (day !== lastDay) {
                out.push({ kind: 'day', key: `day-${day}`, label: day });
                lastDay = day;
            }
            out.push({ kind: 'msg', key: entry.id, entry });
        }
        return out;
    });

    const loadBrowse = async () => {
        const id = botId();
        if (!id) return;
        setLoading(true);
        const a = anchorId();
        const params: { fromUtc: string; toUtc: string; take: number; anchorId?: string; skip?: number } = {
            fromUtc: new Date(from()).toISOString(),
            toUtc: new Date(to()).toISOString(),
            take: PAGE_SIZE,
        };
        if (a) params.anchorId = a;
        else params.skip = page() * PAGE_SIZE;
        const seq = ++reqSeq;
        const res = await api.botRegistry.getChatLog(id, params);
        if (seq !== reqSeq) return;
        setLoading(false);
        if (isOk(res.data)) {
            const list: ChatEntry[] = (res.data.value.entries ?? []) as ChatEntry[];
            setEntries(list);
            setTotal(res.data.value.total ?? 0);
            const skipBack = res.data.value.skip ?? 0;
            if (a) setPage(Math.floor(skipBack / PAGE_SIZE));
            const doScroll = () => {
                if (!listEl) return;
                if (a) {
                    const el = listEl.querySelector(`[data-msg-id="${a}"]`) as HTMLElement | null;
                    if (el) {
                        const offset = el.offsetTop - listEl.clientHeight / 2 + el.clientHeight / 2;
                        listEl.scrollTop = Math.max(0, offset);
                        return;
                    }
                }
                listEl.scrollTop = 0;
            };
            requestAnimationFrame(() => { doScroll(); requestAnimationFrame(doScroll); });
        } else {
            toast?.error(firstError(res.data) ?? t("CHATLOGS_LOAD_FAILED"));
        }
    };

    const loadSearch = async () => {
        const id = botId();
        if (!id) return;
        const q = search().trim();
        if (!q) return;
        setLoading(true);
        const params: { q: string; take: number; context: number; fromUtc?: string; toUtc?: string } = {
            q,
            take: 50,
            context: 2,
        };
        if (searchUsesTimeFilter()) {
            params.fromUtc = new Date(from()).toISOString();
            params.toUtc = new Date(to()).toISOString();
        }
        const seq = ++reqSeq;
        const res = await api.botRegistry.searchChatLog(id, params);
        if (seq !== reqSeq) return;
        setLoading(false);
        if (isOk(res.data)) {
            setMatches((res.data.value.matches ?? []) as SearchMatch[]);
            setSearchTotal(res.data.value.total ?? 0);
        } else {
            toast?.error(firstError(res.data) ?? t("CHATLOGS_LOAD_FAILED"));
        }
    };

    const reload = () => {
        if (isSearching()) void loadSearch();
        else void loadBrowse();
    };

    const reloadFromStart = () => {
        setAnchorId(null);
        setPage(0);
        reload();
    };

    const applyPreset = (hours: number | null) => {
        const now = new Date();
        setFrom(isoLocal(hours === null ? new Date(2000, 0, 1) : new Date(now.getTime() - hours * 3600 * 1000)));
        setTo(isoLocal(now));
        reloadFromStart();
    };

    createEffect(() => {
        if (!botId()) return;
        untrack(() => { reload(); });
    });

    createEffect(() => {
        page();
        untrack(() => {
            if (botId() && !isSearching() && !anchorId()) void loadBrowse();
        });
    });

    let searchTimer: number | undefined;
    const onSearchInput = (v: string) => {
        setSearch(v);
        setAnchorId(null);
        if (searchTimer) window.clearTimeout(searchTimer);
        searchTimer = window.setTimeout(() => {
            setPage(0);
            reload();
        }, 300);
    };

    const jumpToMessage = (msg: ChatEntry) => {
        if (searchTimer) window.clearTimeout(searchTimer);
        setSearch('');
        setMatches([]);
        setFrom(isoLocal(new Date(2000, 0, 1)));
        setTo(isoLocal(new Date()));
        setAnchorId(msg.id);
        void loadBrowse();
    };

    onMount(() => {
        const fromQ = searchParams.from as string | undefined;
        const toQ = searchParams.to as string | undefined;
        if (fromQ) setFrom(fromQ);
        if (toQ) setTo(toQ);
    });

    const renderRow = (e: ChatEntry, variant: 'normal' | 'match' | 'anchor' = 'normal') => (
        <div
            data-msg-id={e.id}
            class={`${styles.row} ${variant === 'match' ? styles.rowHighlight : ''} ${variant === 'anchor' ? styles.rowAnchor : ''}`}
        >
            <span class={styles.timestamp}>{formatDateTime(e.createdAt)}</span>
            <span class={`mc ${styles.message}`}>{formatMinecraftText(e.message)}</span>
        </div>
    );

    return (
        <div class={styles.container}>
            <div class={styles.headerRow}>
                <h1 class={styles.title}><i class="fa-solid fa-comment" /> <Text val="CHATLOGS" /></h1>
                <span class={styles.entryCount}>
                    <Show when={isSearching()} fallback={<>{total()} <Text val="BOT_CHATLOG_ENTRIES" /></>}>
                        {searchTotal()} <Text val="CHATLOGS_MATCHES" />
                    </Show>
                </span>
            </div>

            <div class={styles.layout}>
                <aside class={styles.sidebar}>
                    <div class={`fcard ${styles.sidebarGroup}`}>
                        <span class={styles.sidebarHeading}><Text val="CHATLOGS_BOT" /></span>
                        <div class={styles.botRow}>
                            <Show when={activeBot()} fallback={
                                <span class={styles.fieldLabel}>
                                    <Text val="CHATLOGS_SELECT_BOT" />
                                </span>
                            }>
                                <span class={styles.botName}>{activeBot()!.minecraftUsername}</span>
                            </Show>
                            <Show when={!isSearching()}>
                                <button class="btn btn-primary btn-sm" disabled={loading() || !botId()} onClick={reloadFromStart}>
                                    <i class="fa-solid fa-rotate" /> <Text val="BOT_CHATLOG_REFRESH" />
                                </button>
                            </Show>
                        </div>
                    </div>

                    <div class={`fcard ${styles.sidebarGroup}`}>
                        <span class={styles.sidebarHeading}><Text val="CHATLOGS_SEARCH" /></span>
                        <label class={styles.field}>
                            <SearchInput value={search()} onInput={onSearchInput} placeholder={t('CHATLOGS_SEARCH')} ariaLabel={t('CHATLOGS_SEARCH')} />
                        </label>
                        <label class={styles.checkboxField}>
                            <input
                                type="checkbox"
                                checked={searchUsesTimeFilter()}
                                onChange={e => { setSearchUsesTimeFilter(e.currentTarget.checked); if (isSearching()) void loadSearch(); }}
                            />
                            <span><Text val="CHATLOGS_SEARCH_WITHIN_RANGE" /></span>
                        </label>
                    </div>

                    <div class={`fcard ${styles.sidebarGroup}`}>
                        <span class={styles.sidebarHeading}><Text val="BOT_CHATLOG_HISTORY_TITLE" /></span>
                        <div class={styles.presets}>
                            <For each={presets}>{(p) => (
                                <button class="btn btn-secondary btn-sm" onClick={() => applyPreset(p.hours)} disabled={!botId()}>
                                    <Text val={p.key} />
                                </button>
                            )}</For>
                        </div>
                        <label class={styles.field}>
                            <span class={styles.fieldLabel}><Text val="BOT_CHATLOG_FROM" /></span>
                            <input type="datetime-local" value={from()} onInput={e => setFrom(e.currentTarget.value)} />
                        </label>
                        <label class={styles.field}>
                            <span class={styles.fieldLabel}><Text val="BOT_CHATLOG_TO" /></span>
                            <input type="datetime-local" value={to()} onInput={e => setTo(e.currentTarget.value)} />
                        </label>
                    </div>
                </aside>

                <div class={styles.main}>
                    <Show when={anchorId() && !isSearching()}>
                        <div class={styles.anchorBanner}>
                            <i class="fa-solid fa-location-crosshairs" />
                            <span style={{ flex: 1 }}><Text val="CHATLOGS_JUMPED_TO_MESSAGE" /></span>
                            <button class="btn btn-secondary btn-sm" onClick={() => { setAnchorId(null); void loadBrowse(); }}>
                                <i class="fa-solid fa-xmark" /> <Text val="CHATLOGS_CLEAR_ANCHOR" />
                            </button>
                        </div>
                    </Show>

                    <div ref={listEl} class={`fcard ${styles.list}`}>
                <Show when={botId()} fallback={
                    <EmptyState icon="fa-robot" title={t('CHATLOGS_SELECT_BOT')} />
                }>
                    <Show when={!loading()} fallback={
                        <div class={styles.spinnerCenter}>
                            <i class="fa-solid fa-spinner fa-spin fa-2x" />
                        </div>
                    }>
                        <Show when={isSearching()} fallback={
                            <Show when={entries().length > 0} fallback={
                                <EmptyState icon="fa-comment-slash" title={t('BOT_CHATLOG_EMPTY')} />
                            }>
                                <div class={styles.entries}>
                                    <For each={rows()}>{(r) => (
                                        r.kind === 'day'
                                            ? <div class={styles.daySeparator}><span>{r.label}</span></div>
                                            : renderRow(r.entry, r.entry.id === anchorId() ? 'anchor' : 'normal')
                                    )}</For>
                                </div>
                            </Show>
                        }>
                            <Show when={matches().length > 0} fallback={
                                <EmptyState icon="fa-magnifying-glass" title={t('CHATLOGS_NO_MATCHES')} />
                            }>
                                <div class={styles.results}>
                                    <For each={matches()}>{(m) => (
                                        <button
                                            type="button"
                                            class={styles.matchCard}
                                            onClick={() => jumpToMessage(m.match)}
                                            aria-label={t('CHATLOGS_JUMP_TO_FULL_LOG')}
                                            title={t('CHATLOGS_JUMP_TO_FULL_LOG')}
                                        >
                                            <For each={m.before}>{(b) => renderRow(b)}</For>
                                            {renderRow(m.match, 'match')}
                                            <For each={m.after}>{(a) => renderRow(a)}</For>
                                        </button>
                                    )}</For>
                                </div>
                            </Show>
                        </Show>
                    </Show>
                </Show>
                    </div>

                    <Show when={!isSearching() && total() > PAGE_SIZE}>
                        <div class={styles.pagination}>
                            <button class="btn btn-secondary btn-sm" disabled={page() <= 0 || loading()} onClick={() => { setAnchorId(null); setPage(p => Math.max(0, p - 1)); }}>
                                <i class="fa-solid fa-chevron-left" /> <Text val="CHATLOGS_NEWER" />
                            </button>
                            <span class={styles.pageLabel}>{page() + 1} / {pageCount()}</span>
                            <button class="btn btn-secondary btn-sm" disabled={page() + 1 >= pageCount() || loading()} onClick={() => { setAnchorId(null); setPage(p => p + 1); }}>
                                <Text val="CHATLOGS_OLDER" /> <i class="fa-solid fa-chevron-right" />
                            </button>
                        </div>
                    </Show>
                </div>
            </div>
        </div>
    );
}
